// Editor state of a product: `read/set` work on the product object or, for a localized
// field, on the current language of its framework localization entry (the
// field holds the key, the entry's `_id` is that key); `dirty` tracks the
// object and every touched entry; `save` writes them in order.
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { LoadRawProduct, SaveProduct, LoadLocalizations, LoadRawLocalizations, SaveLocalization } from "../../../AdminShared/api"
import { errorMessage } from "../../../AdminShared/ui"
import { LOCALIZED_FIELDS, CREATOR_FIELDS, SERVER_FIELDS } from "./editor-config"

// catalog fields whose store field of the same name means something else:
// `category` is the catalog's category list here, the store category there.
// `cost` is the catalog's price as a tier code (EUR_1299): the save writes it
// into the StorePrice the store product's `price` points at, and the store
// form has no such field to take back
const NOT_THE_FORMS = new Set(["category", "pid", "cost"])
import { useAdminLanguage } from "../../../AdminShared/languages"
import { humanize, summarize, recordDiff } from "aldoo-ra/CMS/content-editor/save-summary"

const getPath = (obj, path) => String(path).split(".").reduce((o, k) => (o === undefined || o === null ? undefined : o[k]), obj)

const setPath = (obj, path, value) => {
  const keys = String(path).split(".")
  const next = Array.isArray(obj) ? [...obj] : { ...obj }
  let cursor = next
  keys.forEach((key, i) => {
    if (i === keys.length - 1) {
      cursor[key] = value
      return
    }
    const child = cursor[key]
    cursor[key] = Array.isArray(child) ? [...child] : { ...(child || {}) }
    cursor = cursor[key]
  })
  return next
}

const locId = (value) => (value && typeof value === "object" && value._id ? String(value._id) : value ? String(value) : undefined)

// `shared`: the product dialog's language state ({ language, setLanguage }) — the editor reads and
// writes in it, and a tab switching the language switches the whole dialog's; without it the
// editor keeps a language of its own
// the names the summary of a save gives the document's fields (the rest are spelled out from their key)
const FIELD_LABELS = { name: "Title", author_display_name: "Author display name", bookTrailer: "Trailer", fields: "Fields", ageRating: "Age rating", presentationOrder: "Presentation order", free_shipping: "Free shipping", cost: "Price" }

export function useProductEditor(pid, shared = null) {
  const [product, setProduct] = useState(null)
  const [loc, setLoc] = useState({})
  const [urls, setUrls] = useState({})
  const [media, setMedia] = useState([])
  const [mediaRoot, setMediaRoot] = useState("")
  const [error, setError] = useState("")
  // the primary language of the framework localization system; the list follows it
  const [ownLanguage, setOwnLanguage] = useAdminLanguage()
  const language = shared?.language || ownLanguage
  const setLanguage = shared?.setLanguage || setOwnLanguage
  const [dirtyObj, setDirtyObj] = useState(false)
  const [dirtyLoc, setDirtyLoc] = useState(() => new Set())
  const [savePending, setSavePending] = useState(false)
  const changed = useRef(false)
  const reviewSent = useRef(false)
  // the top-level fields edited since the last save: the product dialog saves
  // its own form after this editor, and a form still holding what it loaded
  // would write the old values back over these (a stock of 10 saved as null)
  const touched = useRef(new Set())

  // ---- undo / redo ------------------------------------------------------
  // Editing is a series of small writes to two places: the product document
  // and the localization entries. A step back is therefore both of them as
  // they were, which is what is kept here. Writes that belong to one gesture
  // (a drag moving a keyframe, typing in a field) are coalesced by their path
  // so one undo takes back the gesture rather than a pixel of it.
  const past = useRef([])
  const future = useRef([])
  const lastStep = useRef({ key: null, at: 0 })
  const [steps, setSteps] = useState({ undo: 0, redo: 0 })
  const productRef = useRef(null)
  // the document and its texts as loaded: what `changes()` compares against
  const loadedRef = useRef({ product: null, loc: {} })
  const locRef = useRef({})
  const dirtyRef = useRef({ obj: false, loc: new Set() })
  const COALESCE_MS = 600
  const HISTORY_LIMIT = 200

  const record = useCallback((key) => {
    const now = Date.now()
    const same = key && lastStep.current.key === key && now - lastStep.current.at < COALESCE_MS
    lastStep.current = { key, at: now }
    if (same) return
    past.current = [
      ...past.current.slice(-HISTORY_LIMIT + 1),
      { product: productRef.current, loc: locRef.current, dirtyObj: dirtyRef.current.obj, dirtyLoc: dirtyRef.current.loc },
    ]
    future.current = []
    setSteps({ undo: past.current.length, redo: 0 })
  }, [])

  const restore = useCallback((snapshot) => {
    // what a step back changes is an edit like any other: the save sends it
    const current = productRef.current || {}
    const next = snapshot.product || {}
    let differs = false
    for (const key of new Set([...Object.keys(current), ...Object.keys(next)])) {
      if (current[key] === next[key]) continue
      touched.current.add(key)
      differs = true
    }
    setProduct(snapshot.product)
    setLoc(snapshot.loc)
    setDirtyObj(snapshot.dirtyObj || differs)
    setDirtyLoc(new Set(snapshot.dirtyLoc))
  }, [])

  const undo = useCallback(() => {
    const previous = past.current[past.current.length - 1]
    if (!previous) return false
    past.current = past.current.slice(0, -1)
    future.current = [
      ...future.current,
      { product: productRef.current, loc: locRef.current, dirtyObj: dirtyRef.current.obj, dirtyLoc: dirtyRef.current.loc },
    ]
    lastStep.current = { key: null, at: 0 }
    restore(previous)
    setSteps({ undo: past.current.length, redo: future.current.length })
    return true
  }, [restore])

  const redo = useCallback(() => {
    const next = future.current[future.current.length - 1]
    if (!next) return false
    future.current = future.current.slice(0, -1)
    past.current = [
      ...past.current,
      { product: productRef.current, loc: locRef.current, dirtyObj: dirtyRef.current.obj, dirtyLoc: dirtyRef.current.loc },
    ]
    lastStep.current = { key: null, at: 0 }
    restore(next)
    setSteps({ undo: past.current.length, redo: future.current.length })
    return true
  }, [restore])

  const load = useCallback(async () => {
    const [productRes, prepared, raw] = await Promise.all([LoadRawProduct(pid, true), LoadLocalizations(pid), LoadRawLocalizations(pid)])
    const err = errorMessage(productRes)
    if (err || !productRes?.content) {
      setError(err || "Book with that id not found")
      return null
    }
    const content = productRes.content
    // creators arrive populated (author) or as ids: the editor keeps ids
    for (const field of CREATOR_FIELDS) content[field] = (content[field] || []).map(locId).filter(Boolean)
    setProduct(content)
    loadedRef.current = { product: content, loc: Object.fromEntries((raw?.entries || []).map((e) => [String(e._id), { ...e, translations: e.translations || {} }])) }
    setMedia(productRes.media || [])
    setMediaRoot(productRes.mediaRoot || "")
    setLoc(Object.fromEntries((raw?.entries || []).map((e) => [String(e._id), { ...e, translations: e.translations || {} }])))
    setUrls(Object.fromEntries((prepared?.entries || []).map((e) => [String(e._id), e.translations])))
    setDirtyObj(false)
    setDirtyLoc(new Set())
    // a freshly loaded book has nothing to take back
    past.current = []
    future.current = []
    setSteps({ undo: 0, redo: 0 })
    return content
  }, [pid])

  useEffect(() => {
    load()
  }, [load])

  const defaultLanguage = product?.languages?.[0] || "en"

  const read = useCallback(
    (path, raw) => {
      if (!product) return undefined
      if (!raw && LOCALIZED_FIELDS.includes(path)) {
        const translations = loc[locId(product[path])]?.translations
        if (!translations) return ""
        return translations[language] ?? translations[defaultLanguage] ?? ""
      }
      return getPath(product, path)
    },
    [product, loc, language, defaultLanguage]
  )

  // One entry of the product's fields map in every language at once — a code (the SKU, the
  // EAN) is the same whatever the language the page is read in
  const setFieldEverywhere = useCallback(
    (key, value) => {
      record(`fields:${key}`)
      let id = locId(productRef.current?.fields)
      if (!id) {
        id = `Product-fields-${pid}`
        setProduct((b) => ({ ...b, fields: id }))
        setDirtyObj(true)
        touched.current.add("fields")
      }
      setLoc((entries) => {
        const translations = { ...(entries[id]?.translations || {}) }
        const languages = Object.keys(translations).length ? Object.keys(translations) : [language]
        for (const code of languages) translations[code] = { ...(translations[code] && typeof translations[code] === "object" ? translations[code] : {}), [key]: value }
        return { ...entries, [id]: { ...(entries[id] || {}), type: entries[id]?.type || "object", translations } }
      })
      setDirtyLoc((ids) => new Set(ids).add(id))
    },
    [language, pid, record]
  )

  // a localized field's text in the editing language alone, without the fallback to the
  // default one: an input shows what is written in its language, the fallback as its placeholder
  const readOwn = useCallback(
    (path) => {
      if (!product || !LOCALIZED_FIELDS.includes(path)) return undefined
      return loc[locId(product[path])]?.translations?.[language] ?? ""
    },
    [product, loc, language]
  )

  // What a save would write, for the dialog's summary: [{ label, before, after }] — the
  // document's fields edited here, and each text changed in each language
  const changes = useCallback(() => {
    const out = []
    const loaded = loadedRef.current
    const current = productRef.current || {}
    const same = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null)
    for (const key of touched.current) {
      // a localized field holds its entry's id: its text is answered below
      if (LOCALIZED_FIELDS.includes(key)) continue
      if (same(current[key], loaded.product?.[key])) continue
      out.push({
        label: humanize(FIELD_LABELS[key] || key),
        before: summarize(loaded.product?.[key]),
        after: summarize(current[key]),
        // the field as loaded; nothing left to save once no field differs
        revert: () => {
          setProduct((p) => ({ ...p, [key]: loadedRef.current.product?.[key] }))
          touched.current.delete(key)
          if (!touched.current.size) setDirtyObj(false)
        },
      })
    }
    for (const id of dirtyRef.current.loc) {
      const field = LOCALIZED_FIELDS.find((name) => locId(current[name]) === id)
      const now = locRef.current[id]?.translations || {}
      const before = loaded.loc[id]?.translations || {}
      for (const language of new Set([...Object.keys(now), ...Object.keys(before)])) {
        if (same(now[language], before[language])) continue
        out.push({
          label: `${humanize(FIELD_LABELS[field] || field || "Text")} (${String(language).toUpperCase()})`,
          // a map (the fields) by its entries that changed: "sku PS-1" → "sku PS-2"
          ...(before[language] && now[language] && typeof before[language] === "object" && typeof now[language] === "object" && !Array.isArray(now[language])
            ? recordDiff(before[language], now[language])
            : { before: summarize(before[language]), after: summarize(now[language]) }),
          // that language's text as loaded; the entry has nothing to save once every language is back
          revert: () => {
            const loadedTexts = loadedRef.current.loc[id]?.translations || {}
            const texts = { ...(locRef.current[id]?.translations || {}) }
            if (loadedTexts[language] === undefined) delete texts[language]
            else texts[language] = loadedTexts[language]
            setLoc((entries) => ({ ...entries, [id]: { ...(entries[id] || {}), translations: texts } }))
            const back = Object.keys({ ...texts, ...loadedTexts }).every((code) => same(texts[code], loadedTexts[code]))
            if (back) setDirtyLoc((ids) => {
              const next = new Set(ids)
              next.delete(id)
              return next
            })
          },
        })
      }
    }
    return out
  }, [])

  productRef.current = product
  locRef.current = loc
  dirtyRef.current = { obj: dirtyObj, loc: dirtyLoc }

  const set = useCallback(
    (path, value, raw) => {
      record(`set:${path}`)
      if (!raw && LOCALIZED_FIELDS.includes(path)) {
        let id = locId(productRef.current?.[path])
        // a field without a key yet gets its own (`Product-<field>-<pid>`); the product save stores it
        if (!id) {
          id = `Product-${path}-${pid}`
          setProduct((b) => ({ ...b, [path]: id }))
          setDirtyObj(true)
          touched.current.add(path)
        }
        setLoc((entries) => ({ ...entries, [id]: { ...(entries[id] || {}), translations: { ...(entries[id]?.translations || {}), [language]: value } } }))
        setDirtyLoc((s) => new Set(s).add(id))
        return
      }
      setProduct((b) => setPath(b, path, value))
      setDirtyObj(true)
      touched.current.add(String(path).split(".")[0])
    },
    [language, pid, record]
  )

  /**
   * Take a value the server already holds — a review state changed by its
   * own action — without marking anything to save.
   */
  const adopt = useCallback((path, value) => {
    setProduct((b) => (b ? setPath(b, path, value) : b))
  }, [])

  /** Localization entry helpers for ids outside the top-level fields (pages) */
  const text = useCallback((id, lang = language) => loc[locId(id)]?.translations?.[lang] ?? "", [loc, language])
  const setText = useCallback(
    (id, value, lang = language) => {
      const key = locId(id)
      if (!key) return
      record(`text:${key}:${lang}`)
      setLoc((entries) => ({ ...entries, [key]: { ...(entries[key] || {}), translations: { ...(entries[key]?.translations || {}), [lang]: value } } }))
      setDirtyLoc((s) => new Set(s).add(key))
    },
    [language, record]
  )
  /** Merge freshly created entries (new page / trigger) into the local table */
  const addEntries = useCallback((entries) => {
    if (!entries?.length) return
    setLoc((current) => ({ ...current, ...Object.fromEntries(entries.map((e) => [String(e._id), { ...e, translations: e.translations || {} }])) }))
  }, [])

  const isSpecified = useCallback(
    (path) => {
      if (!product) return false
      if (LOCALIZED_FIELDS.includes(path)) return !!loc[locId(product[path])]?.translations?.[language]
      return !!getPath(product, path)
    },
    [product, loc, language]
  )

  /** Absolute url of a localized image entry for a language (falls back to the default language) */
  const imageUrl = useCallback(
    (id, lang = language) => {
      const prepared = urls[locId(id)]
      if (!prepared) return ""
      // else whichever language has one: a picture is never hidden for want of one in this language
      return firstUrl(prepared[lang]) || firstUrl(prepared[defaultLanguage]) || firstUrl(prepared.en) || Object.values(prepared).map(firstUrl).find(Boolean) || ""
    },
    [urls, language, defaultLanguage]
  )

  const saveProduct = useCallback(
    async (obj) => {
      const data = {}
      for (const [key, value] of Object.entries(obj)) {
        if (SERVER_FIELDS.includes(key)) continue
        data[key] = value
      }
      const res = await SaveProduct(pid, data)
      const err = errorMessage(res)
      if (err) throw new Error(err)
      return res
    },
    [pid]
  )

  /**
   * Persist the dirty object and localization entries. Resolves to the store
   * fields this save wrote that were edited here — `{ stock: 10 }` — so the
   * dialog's own form can take them before it saves (truthy either way).
   */
  const save = useCallback(
    async (extra = {}) => {
      if (!product) return false
      setSavePending(true)
      try {
        // only what was edited here is written: the document was loaded when
        // the dialog opened, and sent whole it reverted whatever else changed
        // the product meanwhile — a review decision, an uploaded deliverable,
        // the stock check's flag
        const written = { ...extra }
        for (const key of touched.current) if (key in product) written[key] = product[key]
        const edited = {}
        for (const key of [...touched.current, ...Object.keys(extra)]) {
          // the catalog's own meaning of a name the store form also has: its
          // category list, its localization keys, the server's fields
          if (NOT_THE_FORMS.has(key) || LOCALIZED_FIELDS.includes(key) || SERVER_FIELDS.includes(key)) continue
          if (key in written) edited[key] = written[key]
        }
        // a product on sale whose name, price or pictures changed goes back to review: a write says so
        let sentForReview = false
        if ((dirtyObj || Object.keys(extra).length) && Object.keys(written).length) sentForReview = !!(await saveProduct(written))?.sentForReview
        for (const id of dirtyLoc) {
          const entry = loc[id]
          if (!entry) continue
          const res = await SaveLocalization(id, entry.translations || {})
          const err = res ? errorMessage(res) : ""
          if (err) throw new Error(`${entry.field || id}: ${err}`)
          if (res?.sentForReview) sentForReview = true
        }
        reviewSent.current = sentForReview
        changed.current = true
        touched.current = new Set()
        await load()
        return edited
      } finally {
        setSavePending(false)
      }
    },
    [product, loc, dirtyObj, dirtyLoc, saveProduct, load]
  )

  const dirty = dirtyObj || dirtyLoc.size > 0
  // whether the last save sent the product back to review, asked once (catalog-editor's notice)
  const takeSentForReview = useCallback(() => {
    const sent = reviewSent.current
    reviewSent.current = false
    return sent
  }, [])

  return useMemo(
    () => ({ pid, product, loc, urls, media, mediaRoot, error, localizationKeys: product?.localizationKeys || null, language, setLanguage, defaultLanguage, read, readOwn, set, setFieldEverywhere, changes, adopt, text, setText, addEntries, isSpecified, imageUrl, save, takeSentForReview, saveProduct, savePending, dirty, setDirty: () => setDirtyObj(true), reload: load, changed, ready: !!product, undo, redo, canUndo: steps.undo > 0, canRedo: steps.redo > 0 }),
    [pid, product, loc, urls, media, mediaRoot, error, language, defaultLanguage, read, readOwn, set, setFieldEverywhere, changes, adopt, text, setText, addEntries, isSpecified, imageUrl, save, takeSentForReview, saveProduct, savePending, dirty, load, undo, redo, steps]
  )
}

/** First URL of a prepared localized image: a plain url or a responsive { default: [srcset…] }. */
const firstUrl = (value) => {
  if (!value) return ""
  if (typeof value === "string") return value
  const list = value.default || Object.values(value)[0]
  if (!Array.isArray(list) || !list.length) return ""
  // the srcset lists the largest variant first: "url 1024w 1024h, url 512w ..."
  return String(list[list.length - 1]).split(",")[0].trim().split(" ")[0]
}

export const urljoin = (root, path) => {
  if (!path) return ""
  if (/^https?:\/\//.test(path)) return path
  if (!root) return path
  return `${root.replace(/\/+$/, "")}/${String(path).replace(/^\/+/, "")}`
}
